import React from 'react';
import { Link } from 'react-router-dom';
import { Sparkles, Database, ShieldCheck, BarChart3 } from 'lucide-react';

const VALUE_PROPS = [
  { Icon: Database,    text: '14,699 CMS-certified SNFs, 5,400 hospitals, 39k SNF-active physicians — one dataset.' },
  { Icon: BarChart3,   text: 'Managed-care penetration, census mix, hospital alignment, and NPPES enrichment in one workspace.' },
  { Icon: ShieldCheck, text: 'Public CMS + NPPES data only. No PHI. Sourced + refreshed monthly.' },
  { Icon: Sparkles,    text: 'Ask-AI assistant trained on the full graph — answers in seconds, not days.' },
];

/**
 * Shared split-screen shell for /login, /signup, /forgot-password, /reset-password.
 * Matches the landing page's brand palette and typography exactly.
 */
export default function AuthShell({ title, subtitle, children, footer, compactMobile = false }) {
  const hideMobile = compactMobile ? 'hidden lg:block' : '';
  return (
    <div className="min-h-screen bg-[#FBF6EC] text-[#1A2622] flex flex-col lg:flex-row">
      {/* LEFT — brand panel */}
      <aside className={`relative lg:w-[48%] xl:w-[44%] px-6 sm:px-10 lg:px-14 ${compactMobile ? 'py-4' : 'py-8'} lg:py-12 bg-gradient-to-br from-[#F5EFDD] via-[#FBF6EC] to-[#E5DCC4] border-b lg:border-b-0 lg:border-r border-[#E5DCC4] overflow-hidden`}>
        {/* Soft decorative orbs */}
        <div aria-hidden className="pointer-events-none absolute -top-20 -left-16 w-72 h-72 rounded-full bg-[#4A7C5E]/8 blur-3xl" />
        <div aria-hidden className="pointer-events-none absolute bottom-0 right-0 w-96 h-96 rounded-full bg-[#A06628]/6 blur-3xl" />

        <Link to="/" className="relative inline-flex items-center gap-2 group">
          <span className="w-9 h-9 rounded-md bg-[#1A2622] text-[#FBF6EC] flex items-center justify-center font-display text-base">P</span>
          <span className="font-display text-[17px] tracking-tight text-[#1A2622] group-hover:text-[#2D5F4F]">Piece of Cake LTC</span>
        </Link>

        <div className={`relative mt-12 lg:mt-20 ${hideMobile}`}>
          <div className="flex items-center gap-2 text-[11px] font-semibold uppercase tracking-[0.16em] text-[#4A7C5E]">
            <span className="w-1.5 h-1.5 rounded-full bg-[#4A7C5E]" />
            Live from data.cms.gov
          </div>
          <h1 className="mt-5 font-display text-[34px] sm:text-[40px] lg:text-[52px] leading-[1.06] tracking-tight text-[#1A2622] max-w-xl">
            Explore U.S. nursing home ownership, chains, and facility performance.
          </h1>
          <p className="mt-5 text-[15px] sm:text-[16px] text-[#3A4A44] max-w-lg leading-relaxed">
            One workspace for SNF intelligence — from CMS Provider data to NPPES identifiers, managed-care penetration, census mix, and hospital alignment.
          </p>
          <ul className="mt-8 space-y-3.5 max-w-lg">
            {VALUE_PROPS.map(({ Icon, text }, i) => (
              <li key={i} className="flex items-start gap-3">
                <span className="mt-0.5 w-7 h-7 rounded-md bg-white border border-[#E5DCC4] flex items-center justify-center shrink-0">
                  <Icon size={13} className="text-[#4A7C5E]" />
                </span>
                <span className="text-[13.5px] text-[#3A4A44] leading-relaxed">{text}</span>
              </li>
            ))}
          </ul>
        </div>

        <div className={`relative mt-10 lg:mt-16 pt-6 border-t border-[#E5DCC4]/70 text-[11px] text-[#7A857F] max-w-lg ${hideMobile}`}>
          Public CMS + NPPES datasets. No patient data. Refreshed monthly.
        </div>
      </aside>

      {/* RIGHT — form panel */}
      <main className="flex-1 flex flex-col justify-center px-6 sm:px-10 lg:px-14 py-10 lg:py-16">
        <div className="w-full max-w-md mx-auto lg:mx-0">
          <div className="text-[11px] font-semibold uppercase tracking-[0.16em] text-[#4A7C5E]">
            {subtitle}
          </div>
          <h2 className="mt-2 font-display text-[30px] sm:text-[34px] tracking-tight leading-tight">{title}</h2>
          <div className="mt-7">{children}</div>
          {footer && <div className="mt-7 pt-5 border-t border-[#E5DCC4] text-[13px] text-[#3A4A44]">{footer}</div>}
        </div>
      </main>
    </div>
  );
}
